import React from 'react';
import { Editor, EditorContent } from '@tiptap/react';
import {
    PAGE_CONTENT_HEIGHT_PX,
    PAGE_HEIGHT_PX,
    PAGE_MARGIN_PX,
    PAGE_WIDTH_PX,
    PX_PER_INCH,
} from '../utils/pageGeometry';

// Quarter-inch ticks down the first sheet, 1in at a time numbered, with the
// top and bottom margins shaded — the way Google Docs draws its side ruler.
// Sized from the page geometry: it used to be a fixed 1056px of unnumbered
// 20px marks, and when the sheet became true Letter height (1317px) it
// stopped three quarters of the way down.
const RULER_TICKS_PER_INCH = 4;
const RULER_TICKS = Array.from(
    { length: Math.floor(PAGE_HEIGHT_PX / (PX_PER_INCH / RULER_TICKS_PER_INCH)) + 1 },
    (_, i) => i,
);

interface DocumentPagesProps {
    /** Handed down from the editor rather than hooked for here alone */
    t: any;
    showRuler: boolean;
    documentEditor: Editor | null;
    editorKey: number;
    pageCount: number;
    editorContainerRef: React.RefObject<HTMLDivElement | null>;
    leftMargin: number;
    rightMargin: number;
    lineSpacing: number;
    paragraphSpacingBefore: number;
    paragraphSpacingAfter: number;
    keepLinesTogether: boolean;
    keepWithNext: boolean;
    preventSingleLines: boolean;
}

export const DocumentPages: React.FC<DocumentPagesProps> = ({
    t,
    showRuler,
    documentEditor,
    editorKey,
    pageCount,
    editorContainerRef,
    leftMargin,
    rightMargin,
    lineSpacing,
    paragraphSpacingBefore,
    paragraphSpacingAfter,
    keepLinesTogether,
    keepWithNext,
    preventSingleLines,
}) => {
    return (
        <div className="document-editor-content">
            {/* Ruler on far left edge of screen */}
            {showRuler && (
                <div className="editor-ruler" style={{ height: `${PAGE_HEIGHT_PX}px` }}>
                    <div className="ruler-margin" style={{ top: 0, height: `${PAGE_MARGIN_PX}px` }} />
                    <div className="ruler-margin" style={{ bottom: 0, height: `${PAGE_MARGIN_PX}px` }} />
                    {RULER_TICKS.map((i) => {
                        const inch = i % RULER_TICKS_PER_INCH === 0;
                        const half = i % (RULER_TICKS_PER_INCH / 2) === 0;
                        const top = `${(i * PX_PER_INCH) / RULER_TICKS_PER_INCH}px`;
                        return inch && i > 0 ? (
                            <span key={i} className="ruler-number" style={{ top }}>
                                {i / RULER_TICKS_PER_INCH}
                            </span>
                        ) : i > 0 ? (
                            <span key={i} className={`ruler-tick${half ? ' half' : ''}`} style={{ top }} />
                        ) : null;
                    })}
                </div>
            )}
            
            {/* Main Editor Area with Pages - Centered */}
            <div
                className="editor-container"
                style={{
                    '--page-width': `${PAGE_WIDTH_PX}px`,
                    '--page-height': `${PAGE_HEIGHT_PX}px`,
                    '--page-margin': `${PAGE_MARGIN_PX}px`,
                    '--page-content-height': `${PAGE_CONTENT_HEIGHT_PX}px`,
                } as React.CSSProperties}
            >
                <div className="pages-container">
                    {/* One continuous sheet; the paginator draws the page
                        breaks inside it as it measures the text */}
                    <div className="document-page single-page">
                        <div className="page-number">
                            {pageCount === 1
                                ? t.editor.pageSingle
                                : t.editor.pageRange.replace('{count}', String(pageCount))}
                        </div>
                        
                        {documentEditor && (
                            <div 
                                ref={editorContainerRef}
                                className="document-text-editor-wrapper"
                                style={{
                                    marginLeft: `${leftMargin}px`,
                                    marginRight: `${PAGE_WIDTH_PX - rightMargin}px`,
                                    '--line-spacing': lineSpacing,
                                    '--paragraph-spacing-before': `${paragraphSpacingBefore}pt`,
                                    '--paragraph-spacing-after': `${paragraphSpacingAfter}pt`,
                                    '--keep-lines-together': keepLinesTogether ? 'avoid' : 'auto',
                                    '--keep-with-next': keepWithNext ? 'avoid' : 'auto',
                                    '--prevent-single-lines-widows': preventSingleLines ? '2' : '1',
                                    '--prevent-single-lines-orphans': preventSingleLines ? '2' : '1',
                                    '--total-pages': pageCount,
                                    // Let a sheet-mode break bleed to the paper
                                    // edges whatever the ruler margins are
                                    '--page-bleed-left': `${leftMargin}px`,
                                    '--page-bleed-right': `${PAGE_WIDTH_PX - rightMargin}px`,
                                } as React.CSSProperties}
                            >
                                <EditorContent 
                                    editor={documentEditor} 
                                    className="document-text-editor continuous-editor"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};